import React, { useState, useEffect } from 'react';
import { Plus, Printer, Edit, Trash2, Search, BarChart3, History, Download, X, Save, Check, Copy, QrCode, Upload, Eye, FileText, ChevronUp, ChevronDown, Package } from 'lucide-react';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../hooks/useNotifications';
import { useGlobalSearch } from '../hooks/useGlobalSearch';
import { useLanguage } from '../contexts/LanguageContext';
import * as XLSX from 'xlsx';
import Modal from './Modal';

interface Printer {
  id: number;
  name: string;
  alias?: string;
  brand: string;
  model: string;
  type: string;
  category: string;
  ip_address: string;
  serial_number: string;
  mac_address?: string;
  location: string;
  location_id?: number;
  department?: string;
  status: string;
  contract_start_date?: string;
  contract_end_date?: string;
  contract_file_path?: string;
  created_at?: string;
  company_id?: number;
  company?: string;
  latest_counters?: {
    black_white?: number;
    color?: number;
    total?: number;
  };
}

interface Brand {
  id: number;
  name: string;
  models: { id: number; name: string }[];
}

interface Location {
  id: number;
  name: string;
  address: string;
  phone: string;
}

interface Company {
  id: number;
  name: string;
}

interface Driver {
  id: number;
  brand_id: number;
  model_id?: number;
  name: string;
  filename: string;
  file_path: string;
  file_size: number;
  download_count: number;
  is_universal: boolean;
  description?: string;
  uploaded_by: number;
  uploaded_at: string;
  brand_name?: string;
  model_name?: string;
  uploader_name?: string;
}

interface PrinterCounter {
  id: number;
  printer_id: number;
  counter_type: 'black_white' | 'color' | 'total';
  counter_value: number;
  recorded_date: string;
  recorded_by: number;
  recorded_by_name: string;
  notes?: string;
  created_at: string;
}

// IP Adresi Input Component
const IPAddressInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}> = ({ value, onChange, placeholder = "IP Adresi", className = "" }) => {
  const [parts, setParts] = useState(['', '', '', '']);

  useEffect(() => {
    if (value) {
      const ipParts = value.split('.');
      setParts([
        ipParts[0] || '',
        ipParts[1] || '',
        ipParts[2] || '',
        ipParts[3] || ''
      ]);
    } else {
      setParts(['', '', '', '']);
    }
  }, [value]);

  const handlePartChange = (index: number, partValue: string) => {
    // Sadece sayıları kabul et ve 0-255 arasında sınırla
    if (partValue === '' || (/^\d+$/.test(partValue) && parseInt(partValue) <= 255)) {
      const newParts = [...parts];
      newParts[index] = partValue;
      setParts(newParts);
      
      // IP adresini birleştir
      const ipAddress = newParts.join('.');
      onChange(ipAddress);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && parts[index] === '' && index > 0) {
      // Önceki input'a geç
      const prevInput = document.getElementById(`ip-part-${index - 1}`);
      prevInput?.focus();
    } else if (e.key === '.' && index < 3) {
      // Sonraki input'a geç
      const nextInput = document.getElementById(`ip-part-${index + 1}`);
      nextInput?.focus();
    }
  };

  return (
    <div className={`flex items-center space-x-1 ${className}`}>
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          <input
            id={`ip-part-${index}`}
            type="text"
            value={part}
            onChange={(e) => handlePartChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            placeholder="0"
            className="w-12 px-2 py-2 text-center border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            maxLength={3}
          />
          {index < 3 && <span className="text-gray-400">.</span>}
        </React.Fragment>
      ))}
    </div>
  );
};

export default function PrintersPage() {
  const { t, language } = useLanguage();
  const { showSuccess, showError, showInfo } = useNotifications();
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState<Printer | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  useGlobalSearch('printers', setSearchTerm);
  const [contractFile, setContractFile] = useState<File | null>(null);
  const [uploadingContract, setUploadingContract] = useState(false);
  const [showCounterModal, setShowCounterModal] = useState(false);
  const [selectedPrinter, setSelectedPrinter] = useState<Printer | null>(null);
  const [showCounterHistory, setShowCounterHistory] = useState(false);
  const [counterHistory, setCounterHistory] = useState<PrinterCounter[]>([]);
  const [printerHistory, setPrinterHistory] = useState<any[]>([]);
  
  // Toner takma state'leri
  const [showTonerModal, setShowTonerModal] = useState(false);
  const [availableToners, setAvailableToners] = useState<any[]>([]);
  const [selectedTonerId, setSelectedTonerId] = useState<number | null>(null);
  const [tonerQuantity, setTonerQuantity] = useState<number>(1);
  const [tonerNotes, setTonerNotes] = useState<string>('');
  const [installingToner, setInstallingToner] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'printers' | 'brands' | 'models' | 'drivers'>('printers');
  const [duplicateWarnings, setDuplicateWarnings] = useState<{type: string, message: string}[]>([]);
  
  // Import modal state'leri
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState('');
  
  // Driver state'leri
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [driverFile, setDriverFile] = useState<File | null>(null);
  const [driverName, setDriverName] = useState('');
  const [driverDescription, setDriverDescription] = useState('');
  const [selectedDriverBrand, setSelectedDriverBrand] = useState<number | null>(null);
  const [selectedDriverModel, setSelectedDriverModel] = useState<number | null>(null);
  const [isUniversalDriver, setIsUniversalDriver] = useState(false);
  const [uploadingDriver, setUploadingDriver] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState<{success: number, errors: string[]} | null>(null);
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [columnMapping, setColumnMapping] = useState<{[key: string]: string}>({});

  // Label printing state'leri
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [selectedPrinters, setSelectedPrinters] = useState<number[]>([]);
  const [labelFilters, setLabelFilters] = useState({
    location: 'all',
    type: 'all',
    category: 'all'
  });
  const [showLabelPreview, setShowLabelPreview] = useState(false);
  const [filteredPrintersForLabel, setFilteredPrintersForLabel] = useState<Printer[]>([]);
  
  // Marka ve model düzenleme için state'ler
  const [editingBrand, setEditingBrand] = useState<{id: number, name: string} | null>(null);
  const [editingModel, setEditingModel] = useState<{id: number, name: string, brand_id: number} | null>(null);
  const [newBrandName, setNewBrandName] = useState('');
  const [newModelName, setNewModelName] = useState('');
  const [selectedBrandForModel, setSelectedBrandForModel] = useState<number | null>(null);
  
  // Filtre state'leri
  const [typeFilter, setTypeFilter] = useState<'all' | 'office' | 'industrial'>('all');
  const [companyFilter, setCompanyFilter] = useState<string>('all');
  const [brandFilter, setBrandFilter] = useState<string>('all');
  const [ipFilter, setIpFilter] = useState<string>('');
  const [serialFilter, setSerialFilter] = useState<string>('');
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // Pagination state'leri
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  
  // Sorting state'leri
  const [sortField, setSortField] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  
  const { user } = useAuth();

  // Sıralama fonksiyonu
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sıralama ikonu bileşeni
  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) {
      return <ChevronUp className="w-4 h-4 opacity-30" />;
    }
    return sortDirection === 'asc' ? 
      <ChevronUp className="w-4 h-4 text-primary" /> : 
      <ChevronDown className="w-4 h-4 text-primary" />;
  };


  const [formData, setFormData] = useState({
    name: '',
    alias: '',
    brand: '',
    model: '',
    type: 'Lazer',
    category: 'industrial',
    ip_address: '',
    serial_number: '',
    mac_address: '',
    location: '',
    location_id: '',
    department: '',
    status: 'active',
    contract_start_date: '',
    contract_end_date: '',
    company_id: ''
  });

  const [counterData, setCounterData] = useState({
    counter_type: 'black_white' as 'black_white' | 'color' | 'total',
    counter_value: '',
    recorded_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  useEffect(() => {
    fetchPrinters();
    fetchBrands();
    fetchLocations();
    fetchCompanies();
    fetchDrivers();
    
    // Kullanıcının lokasyonunu form'a ayarla
    if (user?.location_id && locations.length > 0) {
      const userLocation = locations.find(loc => loc.id === user.location_id);
      if (userLocation) {
        setFormData(prev => ({
          ...prev,
          location: userLocation.name
        }));
      }
    }
  }, [user, locations]);

  // Kullanıcının lokasyonuna göre yazıcı isim formatını oluştur
  const generatePrinterName = (locationName: string) => {
    if (!locationName) return '';
    
    // Lokasyon adından kod çıkar (örn: "TR-ALI" -> "TR-ALI")
    const locationCode = locationName.split(' ')[0]; // İlk kelimeyi al
    return `${locationCode}-PRT-001`;
  };

  // Kullanıcının lokasyonuna göre yazıcı ismini ayarla
  useEffect(() => {
    if (user?.location && !editingPrinter) {
      const suggestedName = generatePrinterName(user.location.name);
      setFormData(prev => ({
        ...prev,
        name: suggestedName
      }));
    }
  }, [user?.location, editingPrinter]);

  // Label printing functions
  const applyLabelFilters = () => {
    let filtered = [...printers];

    if (labelFilters.location !== 'all') {
      filtered = filtered.filter(p => p.location === labelFilters.location);
    }

    if (labelFilters.type !== 'all') {
      filtered = filtered.filter(p => p.type === labelFilters.type);
    }

    if (labelFilters.category !== 'all') {
      filtered = filtered.filter(p => p.category === labelFilters.category);
    }

    setFilteredPrintersForLabel(filtered);
  };

  // Mesajları otomatik temizle
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Label filtrelerini uygula
  useEffect(() => {
    applyLabelFilters();
  }, [labelFilters, printers]);


  const fetchPrinters = async () => {
    try {
      setError(null);
      const response = await apiService.get('/printers');
      
      // Edge tarayıcısı için daha robust veri kontrolü
      let printersData = [];
      if (Array.isArray(response)) {
        printersData = response;
      } else if (response && Array.isArray(response.data)) {
        printersData = response.data;
      } else if (response && response.printers && Array.isArray(response.printers)) {
        printersData = response.printers;
      } else {
        console.warn('Unexpected response format:', response);
        printersData = [];
      }
      
      setPrinters(printersData);
    } catch (error) {
      console.error('Yazıcılar yüklenirken hata:', error);
      
      // Edge tarayıcısı için daha detaylı hata mesajı
      let errorMessage = 'Yazıcılar yüklenirken bir hata oluştu';
      if (error instanceof Error) {
        if (error.message.includes('Network')) {
          errorMessage = 'Ağ bağlantısı hatası. Lütfen internet bağlantınızı kontrol edin.';
        } else if (error.message.includes('JSON')) {
          errorMessage = 'Sunucudan gelen veri formatı hatası.';
        } else if (error.message.includes('401') || error.message.includes('403')) {
          errorMessage = 'Yetkilendirme hatası. Lütfen tekrar giriş yapın.';
        } else {
          errorMessage = `Hata: ${error.message}`;
        }
      }
      
      setError(errorMessage);
      setPrinters([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await apiService.get('/printers/brands');
      
      // Edge tarayıcısı için daha robust veri kontrolü
      let brandsData = [];
      if (Array.isArray(response)) {
        brandsData = response;
      } else if (response && Array.isArray(response.data)) {
        brandsData = response.data;
      } else if (response && response.brands && Array.isArray(response.brands)) {
        brandsData = response.brands;
      } else {
        console.warn('Unexpected brands response format:', response);
        brandsData = [];
      }
      
      setBrands(brandsData);
    } catch (error) {
      console.error('Markalar yüklenirken hata:', error);
      setBrands([]);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await apiService.get('/locations');
      
      // Edge tarayıcısı için daha robust veri kontrolü
      let locationsData = [];
      if (Array.isArray(response)) {
        locationsData = response;
      } else if (response && Array.isArray(response.data)) {
        locationsData = response.data;
      } else if (response && response.locations && Array.isArray(response.locations)) {
        locationsData = response.locations;
      } else {
        console.warn('Unexpected locations response format:', response);
        locationsData = [];
      }
      
      setLocations(locationsData);
    } catch (error) {
      console.error('Lokasyonlar yüklenirken hata:', error);
      setLocations([]);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await apiService.get('/printers/companies');
      
      // Edge tarayıcısı için daha robust veri kontrolü
      let companiesData = [];
      if (Array.isArray(response)) {
        companiesData = response;
      } else if (response && Array.isArray(response.data)) {
        companiesData = response.data;
      } else if (response && response.companies && Array.isArray(response.companies)) {
        companiesData = response.companies;
      } else {
        console.warn('Unexpected companies response format:', response);
        companiesData = [];
      }
      
      setCompanies(companiesData);
    } catch (error) {
      console.error('Firmalar yüklenirken hata:', error);
      setCompanies([]);
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await apiService.get('/printers/drivers');
      let driversData = [];
      if (Array.isArray(response)) {
        driversData = response;
      } else if (response && Array.isArray(response.data)) {
        driversData = response.data;
      } else if (response && response.drivers && Array.isArray(response.drivers)) {
        driversData = response.drivers;
      } else {
        console.warn('Unexpected drivers response format:', response);
        driversData = [];
      }
      setDrivers(driversData);
    } catch (error) {
      console.error('Driver\'lar yüklenirken hata:', error);
      setDrivers([]);
    }
  };

  const handleDriverUpload = async () => {
    if (!driverFile || !selectedDriverBrand || !driverName.trim()) {
      setError('Lütfen tüm gerekli alanları doldurun.');
      return;
    }

    // Dosya boyutu kontrolü (200MB)
    const maxSize = 200 * 1024 * 1024; // 200MB
    if (driverFile.size > maxSize) {
      setError('Dosya boyutu 200MB\'dan büyük olamaz.');
      return;
    }

    // Dosya formatı kontrolü
    const allowedExtensions = ['.zip', '.rar', '.exe'];
    const fileExtension = driverFile.name.toLowerCase().substring(driverFile.name.lastIndexOf('.'));
    if (!allowedExtensions.includes(fileExtension)) {
      setError('Sadece .zip, .rar ve .exe dosyaları yüklenebilir.');
      return;
    }

    try {
      setUploadingDriver(true);
      setError(null);

      const formData = new FormData();
      formData.append('driver', driverFile);
      formData.append('name', driverName);
      formData.append('description', driverDescription);
      formData.append('brand_id', selectedDriverBrand.toString());
      if (selectedDriverModel && !isUniversalDriver) {
        formData.append('model_id', selectedDriverModel.toString());
      }
      formData.append('is_universal', isUniversalDriver.toString());

      const response = await apiService.uploadDriver(formData);
      
      setSuccess('Driver başarıyla yüklendi!');
      showSuccess('Driver Yüklendi', `${driverName} driver'ı başarıyla yüklendi`);
      await fetchDrivers();
      
      // Form'u temizle
      setDriverFile(null);
      setDriverName('');
      setDriverDescription('');
      setSelectedDriverBrand(null);
      setSelectedDriverModel(null);
      setIsUniversalDriver(false);
      setShowDriverModal(false);
      setError(null);
      
    } catch (error) {
      console.error('Driver upload error:', error);
      let errorMessage = 'Driver yüklenirken hata oluştu';
      
      if (error instanceof Error) {
        // Multer hatalarını daha anlaşılır hale getir
        if (error.message.includes('200MB') || error.message.includes('LIMIT_FILE_SIZE')) {
          errorMessage = 'Dosya boyutu 200MB\'dan büyük olamaz.';
        } else if (error.message.includes('.zip') || error.message.includes('.rar') || error.message.includes('.exe')) {
          errorMessage = 'Sadece .zip, .rar ve .exe dosyaları yüklenebilir.';
        } else if (error.message.includes('Network') || error.message.includes('Failed to fetch')) {
          errorMessage = 'Bağlantı hatası. Lütfen internet bağlantınızı kontrol edin.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setError(errorMessage);
      showError('Driver Yükleme Hatası', errorMessage);
    } finally {
      setUploadingDriver(false);
    }
  };

  const handleDriverDownload = async (driver: Driver) => {
    try {
      const response = await fetch(`/api/printers/drivers/${driver.id}/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          setError('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return;
        }
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = driver.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      // Download notification
      showInfo('Driver İndirildi', `${user?.name || 'Kullanıcı'} ${driver.name} driver'ını indirdi`);
      
      // Download count'u güncellemek için drivers'ı yeniden yükle
      await fetchDrivers();
    } catch (error) {
      console.error('Download error:', error);
      setError('Driver indirilirken hata oluştu: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'));
    }
  };

  const handleDriverDelete = async (driverId: number) => {
    if (!confirm('Bu driver\'ı silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const driver = drivers.find(d => d.id === driverId);
      await apiService.delete(`/printers/drivers/${driverId}`);
      setSuccess('Driver başarıyla silindi!');
      showSuccess('Driver Silindi', `${driver?.name || 'Driver'} başarıyla silindi`);
      await fetchDrivers();
    } catch (error) {
      console.error('Driver delete error:', error);
      setError('Driver silinirken hata oluştu: ' + (error instanceof Error ? error.message : 'Bilinmeyen hata'));
      showError('Silme Hatası', 'Driver silinirken hata oluştu');
    }
  };

  // Drag & Drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    const file = files[0];
    
    if (file) {
      // Check file type
      const allowedExtensions = ['.zip', '.rar', '.exe'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (!allowedExtensions.includes(fileExtension)) {
        setError('Sadece .zip, .rar ve .exe dosyaları yüklenebilir.');
        return;
      }
      
      // Check file size (200MB)
      const maxSize = 200 * 1024 * 1024;
      if (file.size > maxSize) {
        setError('Dosya boyutu 200MB\'dan büyük olamaz.');
        return;
      }
      
      setDriverFile(file);
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError(null);
      setSuccess(null);
      
      // Check for duplicates before submitting
      const hasDuplicates = await checkDuplicates(formData.ip_address, formData.serial_number);
      if (hasDuplicates) {
        setError('IP adresi veya seri numarası çakışması tespit edildi. Lütfen kontrol edin.');
        return;
      }
      
      let printerId;
      
      // Convert company_id to number before sending
      const submitData = {
        ...formData,
        company_id: formData.company_id ? parseInt(formData.company_id) : null
      };

      if (editingPrinter) {
        await apiService.put(`/printers/${editingPrinter.id}`, submitData);
        printerId = editingPrinter.id;
        setSuccess('Yazıcı başarıyla güncellendi');
        showSuccess('Yazıcı Güncellendi', `${formData.name} yazıcısı başarıyla güncellendi`);
      } else {
        const response = await apiService.post('/printers', submitData);
        printerId = response.id;
        setSuccess('Yazıcı başarıyla eklendi');
        showSuccess('Yazıcı Eklendi', `${formData.name} yazıcısı başarıyla eklendi`);
      }
      
      // Upload contract file if selected for new printer
      if (contractFile && printerId && !editingPrinter) {
        await uploadContractFile(contractFile, printerId);
      }
      
      fetchPrinters();
      resetForm();
    } catch (error: any) {
      console.error('Yazıcı kaydedilirken hata:', error);
      setError(error.response?.data?.message || 'Yazıcı kaydedilirken bir hata oluştu');
    }
  };

  const handleDelete = async (id: number) => {
    const printer = printers.find(p => p.id === id);
    if (confirm('Bu yazıcıyı silmek istediğinizden emin misiniz?')) {
      try {
        setError(null);
        setSuccess(null);
        await apiService.delete(`/printers/${id}`);
        setSuccess('Yazıcı başarıyla silindi');
        showSuccess('Yazıcı Silindi', `${printer?.name || 'Yazıcı'} başarıyla silindi`);
        fetchPrinters();
      } catch (error: any) {
        console.error('Yazıcı silinirken hata:', error);
        setError(error.response?.data?.message || 'Yazıcı silinirken bir hata oluştu');
        showError('Silme Hatası', error.response?.data?.message || 'Yazıcı silinirken bir hata oluştu');
      }
    }
  };

  const resetForm = () => {
    // Kullanıcının lokasyonuna göre yazıcı ismi oluştur
    const suggestedName = user?.location ? generatePrinterName(user.location.name) : '';
    
    setFormData({
      name: suggestedName, brand: '', model: '', type: 'Lazer', category: 'industrial', ip_address: '',
      serial_number: '', location: user?.location?.name || '', location_id: user?.location_id?.toString() || '', status: 'active',
      contract_start_date: '', contract_end_date: '', company_id: ''
    });
    setEditingPrinter(null);
    setShowModal(false);
    setContractFile(null);
    setDuplicateWarnings([]);
  };

  // Marka düzenleme fonksiyonları
  const handleEditBrand = (brand: {id: number, name: string}) => {
    setEditingBrand(brand);
    setNewBrandName(brand.name);
  };

  const handleSaveBrand = async () => {
    if (!editingBrand || !newBrandName.trim()) return;
    
    try {
      setError(null);
      setSuccess(null);
      
      await apiService.put(`/printers/brands/${editingBrand.id}`, { name: newBrandName.trim() });
      setSuccess('Marka başarıyla güncellendi');
      fetchBrands();
      setEditingBrand(null);
      setNewBrandName('');
      
      // Loglama backend'de yapılıyor
    } catch (error: any) {
      console.error('Marka güncelleme hatası:', error);
      setError(error.response?.data?.message || 'Marka güncellenirken hata oluştu');
    }
  };

  const handleAddBrand = async () => {
    if (!newBrandName.trim()) return;
    
    try {
      setError(null);
      setSuccess(null);
      
      await apiService.post('/printers/brands', { name: newBrandName.trim() });
      setSuccess('Marka başarıyla eklendi');
      fetchBrands();
      setNewBrandName('');
      
      // Loglama backend'de yapılıyor
    } catch (error: any) {
      console.error('Marka ekleme hatası:', error);
      setError(error.response?.data?.message || 'Marka eklenirken hata oluştu');
    }
  };

  const handleDeleteBrand = async (id: number, name: string) => {
    if (!confirm(`"${name}" markasını silmek istediğinizden emin misiniz?`)) return;
    
    try {
      setError(null);
      setSuccess(null);
      
      await apiService.delete(`/printers/brands/${id}`);
      setSuccess('Marka başarıyla silindi');
      fetchBrands();
      
      // Loglama backend'de yapılıyor
    } catch (error: any) {
      console.error('Marka silme hatası:', error);
      setError(error.response?.data?.message || 'Marka silinirken hata oluştu');
    }
  };

  // Model düzenleme fonksiyonları
  const handleEditModel = (model: {id: number, name: string, brand_id: number}) => {
    setEditingModel(model);
    setNewModelName(model.name);
    setSelectedBrandForModel(model.brand_id);
  };

  const handleSaveModel = async () => {
    if (!editingModel || !newModelName.trim() || !selectedBrandForModel) return;
    
    try {
      setError(null);
      setSuccess(null);
      
      await apiService.put(`/printers/models/${editingModel.id}`, { 
        name: newModelName.trim(),
        brand_id: selectedBrandForModel
      });
      setSuccess('Model başarıyla güncellendi');
      fetchBrands();
      setEditingModel(null);
      setNewModelName('');
      setSelectedBrandForModel(null);
      
      // Loglama backend'de yapılıyor
    } catch (error: any) {
      console.error('Model güncelleme hatası:', error);
      setError(error.response?.data?.message || 'Model güncellenirken hata oluştu');
    }
  };

  const handleAddModel = async () => {
    if (!newModelName.trim() || !selectedBrandForModel) return;
    
    try {
      setError(null);
      setSuccess(null);
      
      await apiService.post('/printers/models', { 
        name: newModelName.trim(),
        brand_id: selectedBrandForModel
      });
      
      setSuccess('Model başarıyla eklendi');
      fetchBrands();
      setNewModelName('');
      setSelectedBrandForModel(null);
      
      // Loglama backend'de yapılıyor
    } catch (error: any) {
      console.error('Model ekleme hatası:', error);
      setError(error.response?.data?.message || 'Model eklenirken hata oluştu');
    }
  };

  const handleDeleteModel = async (id: number, name: string) => {
    if (!confirm(`"${name}" modelini silmek istediğinizden emin misiniz?`)) return;
    
    try {
      setError(null);
      setSuccess(null);
      
      await apiService.delete(`/printers/models/${id}`);
      setSuccess('Model başarıyla silindi');
      fetchBrands();
      
      // Loglama backend'de yapılıyor
    } catch (error: any) {
      console.error('Model silme hatası:', error);
      setError(error.response?.data?.message || 'Model silinirken hata oluştu');
    }
  };


  const openCounterModal = (printer: Printer) => {
    setSelectedPrinter(printer);
    setShowCounterModal(true);
    setCounterData({
      counter_type: 'black_white',
      counter_value: '',
      recorded_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
  };

  const openHistory = async (printer: Printer) => {
    setSelectedPrinter(printer);
    setShowCounterHistory(true);
    try {
      const response = await apiService.getPrinterHistory(printer.id);
      if (response.success && response.data) {
        setPrinterHistory(response.data.history || []);
      } else {
        setPrinterHistory([]);
      }
    } catch (error) {
      console.error('History yüklenirken hata:', error);
      setPrinterHistory([]);
    }
  };

  const openTonerModal = async (printer: Printer) => {
    setSelectedPrinter(printer);
    setShowTonerModal(true);
    setSelectedTonerId(null);
    setTonerQuantity(1);
    setTonerNotes('');
    
    try {
      const response = await apiService.getAvailableToners(printer.id);
      if (response.success && response.data) {
        setAvailableToners(response.data);
      } else {
        setAvailableToners([]);
      }
    } catch (error) {
      console.error('Toner listesi yüklenirken hata:', error);
      showError('Hata', 'Toner listesi yüklenirken hata oluştu');
      setAvailableToners([]);
    }
  };

  const handleInstallToner = async () => {
    if (!selectedPrinter || !selectedTonerId || tonerQuantity < 1) {
      setError('Lütfen toner ve miktar seçin');
      return;
    }

    try {
      setInstallingToner(true);
      setError(null);
      
      await apiService.installToner(selectedPrinter.id, {
        consumable_id: selectedTonerId,
        quantity: tonerQuantity,
        notes: tonerNotes || undefined
      });

      showSuccess('Başarılı', 'Toner başarıyla takıldı');
      setShowTonerModal(false);
      setSelectedTonerId(null);
      setTonerQuantity(1);
      setTonerNotes('');
      
      // Printers listesini yenile
      await fetchPrinters();
    } catch (error: any) {
      console.error('Toner takma hatası:', error);
      setError(error.message || 'Toner takılırken hata oluştu');
      showError('Hata', error.message || 'Toner takılırken hata oluştu');
    } finally {
      setInstallingToner(false);
    }
  };

  const openCounterHistory = async (printer: Printer) => {
    setSelectedPrinter(printer);
    try {
      const response = await apiService.get(`/printers/${printer.id}/counters`);
      setCounterHistory(Array.isArray(response) ? response : []);
      setShowCounterHistory(true);
    } catch (error) {
      console.error('Sayaç geçmişi yüklenirken hata:', error);
      setCounterHistory([]);
      setShowCounterHistory(true);
    }
  };

  const downloadContract = async (printer: Printer) => {
    if (!printer.contract_file_path) {
      setError('Bu yazıcı için sözleşme dosyası bulunamadı');
      return;
    }
    
    try {
      setError(null);
      const response = await apiService.get(`/printers/${printer.id}/contract`, {
        responseType: 'blob'
      });
      
      // Get file extension from the original filename
      const originalFilename = printer.contract_file_path.split('/').pop() || '';
      const fileExtension = originalFilename.split('.').pop() || 'pdf';
      
      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${printer.name}_sozlesme.${fileExtension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Sözleşme dosyası indirilirken hata:', error);
      setError(error.response?.data?.message || 'Sözleşme dosyası indirilirken bir hata oluştu');
    }
  };

  const uploadContractFile = async (file: File, printerId: number) => {
    try {
      setUploadingContract(true);
      setError(null);
      
      const formDataUpload = new FormData();
      formDataUpload.append('contract', file);
      
      await apiService.post(`/printers/${printerId}/contract`, formDataUpload);
      
      // Yazıcı verilerini yeniden yükle
      await fetchPrinters();
      
      // Düzenlenen yazıcının güncel verilerini al
      const updatedPrinter = printersData.find(p => p.id === printerId);
      if (updatedPrinter) {
        setEditingPrinter(updatedPrinter);
      }
      
      setSuccess('Sözleşme dosyası başarıyla yüklendi');
      setContractFile(null); // Dosya yüklendikten sonra temizle
    } catch (error: any) {
      console.error('Sözleşme dosyası yüklenirken hata:', error);
      setError(error.response?.data?.message || 'Sözleşme dosyası yüklenirken bir hata oluştu');
    } finally {
      setUploadingContract(false);
    }
  };

  const handleCounterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPrinter || !counterData.counter_value) return;
    
    try {
      setError(null);
      setSuccess(null);
      await apiService.post(`/printers/${selectedPrinter.id}/counters`, {
        ...counterData,
        counter_value: parseInt(counterData.counter_value),
        recorded_by: 1 // TODO: Get from auth context
      });
      
      setShowCounterModal(false);
      setSelectedPrinter(null);
      setSuccess('Sayaç bilgisi başarıyla eklendi');
    } catch (error: any) {
      console.error('Sayaç bilgisi eklenirken hata:', error);
      setError(error.response?.data?.message || 'Sayaç bilgisi eklenirken bir hata oluştu');
    }
  };

  const startEdit = (printer: Printer) => {
    // Format dates for input[type="date"] - ensure YYYY-MM-DD format
    const formatDateForInput = (dateStr: string | undefined) => {
      if (!dateStr) return '';
      return dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    };

    setFormData({
      name: printer.name,
      alias: printer.alias || '',
      brand: printer.brand,
      model: printer.model,
      type: printer.type,
      category: printer.category || 'industrial',
      ip_address: printer.ip_address,
      serial_number: printer.serial_number,
      mac_address: printer.mac_address || '',
      location: printer.location,
      location_id: printer.location_id?.toString() || '',
      department: printer.department || '',
      // DB'de durum Türkçe tutuluyor, form ise İngilizce anahtar bekliyor
      status: ({ 'Aktif': 'active', 'Pasif': 'passive', 'Sözleşme Süresi Dolmuş': 'contract_expired', 'Arızalı': 'faulty' } as Record<string, string>)[printer.status] || printer.status,
      contract_start_date: formatDateForInput(printer.contract_start_date),
      contract_end_date: formatDateForInput(printer.contract_end_date),
      company_id: printer.company_id?.toString() || ''
    });
    setEditingPrinter(printer);
    setContractFile(null); // Yeni dosya seçimi için temizle
    setShowModal(true);
  };

  const quickCopy = (printer: Printer) => {
    // Kullanıcının lokasyonuna göre yeni yazıcı ismi oluştur
    const newPrinterName = user?.location ? generatePrinterName(user.location.name) : '';
    
    setFormData({
      name: newPrinterName, // Kullanıcının lokasyonuna göre yazıcı adı
      alias: '', // Takma adı boş bırak
      brand: printer.brand,
      model: printer.model,
      type: printer.type,
      category: printer.category || 'industrial',
      ip_address: '', // IP adresini boş bırak
      serial_number: '', // Seri numarasını boş bırak
      mac_address: '', // MAC adresini boş bırak
      location: printer.location,
      location_id: printer.location_id?.toString() || '',
      department: printer.department || '',
      status: printer.status,
      contract_start_date: '',
      contract_end_date: '',
      company_id: printer.company_id?.toString() || ''
    });
    setEditingPrinter(null); // Yeni yazıcı olarak ekle
    setContractFile(null); // Yeni dosya seçimi için temizle
    setShowModal(true);
  };

  const checkDuplicates = async (ip_address: string, serial_number: string) => {
    try {
      const response = await apiService.post('/printers/check-duplicates', {
        ip_address: ip_address || '',
        serial_number: serial_number || '',
        exclude_id: editingPrinter?.id || null
      });
      
      if (response.has_duplicates) {
        setDuplicateWarnings(response.duplicates.map((dup: any) => ({
          type: dup.type,
          message: dup.message
        })));
        return true;
      } else {
        setDuplicateWarnings([]);
        return false;
      }
    } catch (error) {
      console.error('Çakışma kontrolü hatası:', error);
      return false;
    }
  };

  // Debounced duplicate check
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.ip_address || formData.serial_number) {
        checkDuplicates(formData.ip_address, formData.serial_number);
      }
    }, 1000); // 1 saniye bekle

    return () => clearTimeout(timeoutId);
  }, [formData.ip_address, formData.serial_number, editingPrinter?.id]);

  const filteredPrinters = (printers || []).filter(printer => {
    // Text search - yazıcı adı, marka, lokasyon, IP adresi, seri numarası ve MAC adresi
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = String(printer?.name || '').toLowerCase().includes(searchLower) ||
                          String(printer?.alias || '').toLowerCase().includes(searchLower) ||
      String(printer?.brand || '').toLowerCase().includes(searchLower) ||
      String(printer?.location || '').toLowerCase().includes(searchLower) ||
      String(printer?.ip_address || '').toLowerCase().includes(searchLower) ||
      String(printer?.serial_number || '').toLowerCase().includes(searchLower) ||
      String(printer?.mac_address || '').toLowerCase().includes(searchLower);

    // Category filter (using typeFilter for category filtering)
    const matchesType = typeFilter === 'all' || printer?.category === typeFilter;

    // Company filter
    const matchesCompany = companyFilter === 'all' ||
      (companyFilter === 'no-company' && !printer?.company) ||
      (companyFilter !== 'no-company' && printer?.company === companyFilter);

    // Brand filter
    const matchesBrand = brandFilter === 'all' || printer?.brand === brandFilter;

    // IP filter
    const matchesIp = !ipFilter || String(printer?.ip_address || '').toLowerCase().includes(ipFilter.toLowerCase());

    // Serial filter
    const matchesSerial = !serialFilter || String(printer?.serial_number || '').toLowerCase().includes(serialFilter.toLowerCase());

    // Location filter
    const matchesLocation = locationFilter === 'all' || printer?.location === locationFilter;

    // Status filter
    const matchesStatus = statusFilter === 'all' || printer?.status === statusFilter;

    return matchesSearch && matchesType && matchesCompany && matchesBrand &&
           matchesIp && matchesSerial && matchesLocation && matchesStatus;
  }).sort((a, b) => {
    // Dinamik sıralama
    let aValue = a[sortField as keyof Printer];
    let bValue = b[sortField as keyof Printer];
    
    // Null/undefined değerleri kontrol et
    if (!aValue && !bValue) return 0;
    if (!aValue) return sortDirection === 'asc' ? 1 : -1;
    if (!bValue) return sortDirection === 'asc' ? -1 : 1;
    
    // String değerler için
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const result = aValue.localeCompare(bValue, 'tr', { numeric: true, sensitivity: 'base' });
      return sortDirection === 'asc' ? result : -result;
    }
    
    // Number değerler için
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    // Diğer durumlar için string'e çevir
    const aStr = String(aValue);
    const bStr = String(bValue);
    const result = aStr.localeCompare(bStr, 'tr', { numeric: true, sensitivity: 'base' });
    return sortDirection === 'asc' ? result : -result;
  });

  // Pagination hesaplamaları
  const totalPages = Math.ceil(filteredPrinters.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedPrinters = filteredPrinters.slice(startIndex, endIndex);

  // Sayfa değiştiğinde scroll'u yukarı çek
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Arama, filtre veya sıralama değiştiğinde sayfa numarasını sıfırla
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, typeFilter, companyFilter, brandFilter, ipFilter, serialFilter, locationFilter, statusFilter, sortField, sortDirection]);

  const selectedBrand = brands.find(b => b.name === formData.brand);

  // İstatistik hesaplamaları
  const totalPrinters = printers.length;
  const officePrinters = printers.filter(p => p.category === 'office').length;
  const industrialPrinters = printers.filter(p => p.category === 'industrial').length;

  if (loading) return <div className="flex justify-center p-8">{t('printer.loading')}</div>;

  const generateQRCode = (printer: Printer) => {
    const token = localStorage.getItem('auth_token');
    const params = new URLSearchParams({
      token: token || '',
    });
    const siteUrl = import.meta.env.VITE_SITE_URL || `${window.location.protocol}//${window.location.host}`;
    const url = `${siteUrl}/api/printers/${printer.id}/qr-code?${params}`;
    
    // Yeni sekmede aç
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.location.href = url;
    } else {
      // Popup engellenmişse direkt URL'yi aç
      window.location.href = url;
    }
  };

  const parseImportData = (data: string) => {
    const lines = data.trim().split('\n');
    if (lines.length < 2) {
      return { printers: [], errors: ['En az 2 satır olmalı (başlık + veri)'], columnMapping: {} };
    }
    
    const originalHeaders = lines[0].split('\t').map(h => String(h || '').trim());
    const headers = originalHeaders.map(h => String(h || '').toLowerCase().replace(/[^a-z0-9çğıöşü]/g, ''));
    const errors: string[] = [];
    const printers = [];
    
    // Akıllı sütun eşleştirme
    const columnMapping: {[key: string]: string} = {};
    const columnPatterns = {
      name: ['yazici', 'printer', 'name', 'adi', 'ad', 'yaziciadi', 'printername'],
      brand: ['marka', 'brand', 'manufacturer', 'uretici', 'firma', 'company'],
      model: ['model', 'modeli', 'modeladi', 'modelname', 'modelno', 'modelnumarasi', 'modelkodu', 'modelcode', 'urunmodeli', 'productmodel'],
      type: ['tip', 'type', 'tur', 'kategori', 'category', 'cesit'],
      ip_address: ['ip', 'ipadresi', 'ipadres', 'adres', 'address'],
      serial_number: ['seri', 'serial', 'serinumarsi', 'serino', 'sn'],
      location: ['lokasyon', 'location', 'konum', 'yer', 'place', 'lokasyonu', 'konumu'],
      department: ['bolum', 'department', 'dept', 'birim', 'unit', 'kisim', 'sektor', 'departman'],
      status: ['durum', 'status', 'state', 'hal', 'durumu', 'statu'],
      company: ['firma', 'company', 'sirket', 'kurum', 'organization', 'org']
    };
    
    // Her sütun için en iyi eşleşmeyi bul
    Object.keys(columnPatterns).forEach(field => {
      const patterns = columnPatterns[field as keyof typeof columnPatterns];
      let bestMatch = '';
      let bestScore = 0;
      
      headers.forEach((header, index) => {
        patterns.forEach(pattern => {
          // Tam eşleşme
          if (header === pattern) {
            if (bestScore < 100) {
              bestMatch = originalHeaders[index];
              bestScore = 100;
            }
          }
          // Kısmi eşleşme
          else if (header.includes(pattern) || pattern.includes(header)) {
            const score = Math.min(header.length, pattern.length) / Math.max(header.length, pattern.length) * 80;
            if (score > bestScore) {
              bestMatch = originalHeaders[index];
              bestScore = score;
            }
          }
          // Kelime bazlı eşleşme
          else {
            const headerWords = header.split(/[\s\-_]+/);
            const patternWords = pattern.split(/[\s\-_]+/);
            const commonWords = headerWords.filter(word => 
              patternWords.some(pWord => 
                word.includes(pWord) || pWord.includes(word)
              )
            );
            if (commonWords.length > 0) {
              const score = (commonWords.length / Math.max(headerWords.length, patternWords.length)) * 60;
              if (score > bestScore) {
                bestMatch = originalHeaders[index];
                bestScore = score;
              }
            }
          }
        });
      });
      
      if (bestScore > 30) { // Minimum eşleşme skoru
        columnMapping[field] = bestMatch;
      }
    });
    
    
    // Tanınmayan sütunları uyar
    const recognizedColumns = Object.values(columnMapping);
    const unrecognizedColumns = originalHeaders.filter(col => !recognizedColumns.includes(col));
    if (unrecognizedColumns.length > 0) {
      errors.push(`Tanınmayan sütunlar: ${unrecognizedColumns.join(', ')}`);
    }
    
    // Gerekli sütunları kontrol et
    const requiredFields = ['name', 'brand', 'model', 'location'];
    const missingFields = requiredFields.filter(field => !columnMapping[field]);
    if (missingFields.length > 0) {
      const fieldNames = {
        name: 'Yazıcı Adı',
        brand: 'Marka',
        model: 'Model',
        location: 'Lokasyon'
      };
      errors.push(`Gerekli sütunlar eksik: ${missingFields.map(f => fieldNames[f as keyof typeof fieldNames]).join(', ')}`);
    }
    
    // Veri parse etme
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split('\t');
      if (values.length < headers.length) {
        errors.push(`Satır ${i + 1}: Eksik sütun verisi`);
        continue;
      }
      
      const printer: any = { rowNumber: i + 1 };
      let hasRequiredFields = true;
      
      // Sütun eşleştirmesine göre veri al
      Object.keys(columnMapping).forEach(field => {
        const columnName = columnMapping[field];
        const columnIndex = originalHeaders.indexOf(columnName);
        const value = values[columnIndex]?.trim();
        
        switch (field) {
          case 'name':
            printer.name = value;
            if (!value) {
              errors.push(`Satır ${i + 1}: Yazıcı adı gerekli`);
              hasRequiredFields = false;
            }
            break;
          case 'brand':
            printer.brand = value;
            if (!value) {
              errors.push(`Satır ${i + 1}: Marka gerekli`);
              hasRequiredFields = false;
            }
            break;
          case 'model':
            printer.model = value;
            if (!value) {
              errors.push(`Satır ${i + 1}: Model gerekli`);
              hasRequiredFields = false;
            }
            break;
          case 'type':
            printer.type = value || 'Lazer';
            break;
          case 'ip_address':
            printer.ip_address = value;
            break;
          case 'serial_number':
            printer.serial_number = value;
            break;
          case 'location':
            printer.location = value;
            if (!value) {
              errors.push(`Satır ${i + 1}: Lokasyon gerekli`);
              hasRequiredFields = false;
            }
            break;
          case 'department':
            printer.department = value || '';
            break;
          case 'status':
            printer.status = value || 'Aktif';
            break;
          case 'company':
            printer.company = value;
            break;
        }
      });
      
      if (hasRequiredFields) {
        printers.push(printer);
      }
    }
    
    return { printers, errors, columnMapping };
  };

  const handleDataChange = (data: string) => {
    setImportData(data);
    
    if (data.trim()) {
      const result = parseImportData(data);
      setParsedData(result.printers);
      setParseErrors(result.errors);
      setColumnMapping(result.columnMapping);
    } else {
      setParsedData([]);
      setParseErrors([]);
      setColumnMapping({});
    }
  };

  const handleImport = async () => {
    if (!importData.trim()) {
      setError('Lütfen Excel verilerini yapıştırın');
      return;
    }

    if (parsedData.length === 0) {
      setError('Geçerli yazıcı verisi bulunamadı');
      return;
    }

    // Kısmi import uyarısı
    if (parseErrors.length > 0) {
      const confirmMessage = `${parseErrors.length} satırda hata var. Sadece geçerli ${parsedData.length} satırı import etmek istiyor musunuz?`;
      if (!confirm(confirmMessage)) {
        return;
      }
    }

    setImporting(true);
    setError(null);
    setImportResults(null);

    try {
      
      const response = await fetch(`${import.meta.env.VITE_API_URL}/printers/bulk-import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ printers: parsedData })
      });

      const result = await response.json();
      
      if (response.ok) {
        setImportResults(result);
        setSuccess(`${result.success} yazıcı başarıyla eklendi`);
        setImportData('');
        fetchPrinters(); // Listeyi yenile
      } else {
        setError(result.message || 'İçeri aktarma başarısız');
        setImportResults(result);
      }
    } catch (error) {
      console.error('Import error:', error);
      setError('İçeri aktarma sırasında hata oluştu');
    } finally {
      setImporting(false);
    }
  };

  const resetImportModal = () => {
    setImportData('');
    setImportResults(null);
    setError(null);
    setSuccess(null);
    setParsedData([]);
    setParseErrors([]);
    setColumnMapping({});
  };

  const handlePrinterSelection = (printerId: number) => {
    setSelectedPrinters(prev =>
      prev.includes(printerId)
        ? prev.filter(id => id !== printerId)
        : [...prev, printerId]
    );
  };

  const selectAllFiltered = () => {
    const allIds = filteredPrintersForLabel.map(p => p.id);
    setSelectedPrinters(allIds);
  };

  const deselectAll = () => {
    setSelectedPrinters([]);
  };

  const exportToExcel = () => {
    // Prepare data for Excel export
    const exportData = filteredPrinters.map(printer => ({
      'Yazıcı Adı': printer.name,
      'Takma Ad': printer.alias || '',
      'Şirket': printer.company || '-',
      'Marka': printer.brand,
      'Model': printer.model,
      'Tip': printer.type,
      'Kategori': printer.category || '-',
      'IP Adresi': printer.ip_address || '-',
      'MAC Adresi': printer.mac_address || '-',
      'Seri No': printer.serial_number || '-',
      'Lokasyon': printer.location,
      'Bölüm': printer.department || '-',
      'Durum': printer.status,
      'Sözleşme Başlangıç': printer.contract_start_date || '-',
      'Sözleşme Bitiş': printer.contract_end_date || '-',
      'Kayıt Tarihi': printer.created_at ? new Date(printer.created_at).toLocaleDateString('tr-TR') : '-'
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Set column widths
    const colWidths = [
      { wch: 20 }, // Yazıcı Adı
      { wch: 15 }, // Şirket
      { wch: 12 }, // Marka
      { wch: 15 }, // Model
      { wch: 10 }, // Tip
      { wch: 12 }, // Kategori
      { wch: 15 }, // IP Adresi
      { wch: 17 }, // MAC Adresi
      { wch: 15 }, // Seri No
      { wch: 15 }, // Lokasyon
      { wch: 12 }, // Bölüm
      { wch: 10 }, // Durum
      { wch: 15 }, // Sözleşme Başlangıç
      { wch: 15 }, // Sözleşme Bitiş
      { wch: 12 }  // Kayıt Tarihi
    ];
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Yazıcılar');

    // Generate filename with current date
    const today = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');
    const filename = `yazicilar_listesi_${today}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
  };

  const generateLabelPreview = () => {
    setShowLabelPreview(true);
  };

  const printLabels = async () => {
    const selectedPrinterData = printers.filter(p => selectedPrinters.includes(p.id));
    const token = localStorage.getItem('auth_token');

    // Get QR codes for all selected printers first
    const qrCodes = {};
    for (const printer of selectedPrinterData) {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/printers/${printer.id}/qr-code?token=${token}&embed=true`);
        const html = await response.text();

        // Extract base64 image from the HTML
        const imgMatch = html.match(/src="data:image\/png;base64,([^"]+)"/);
        if (imgMatch) {
          qrCodes[printer.id] = imgMatch[0]; // src="data:image..."
        }
      } catch (error) {
        console.error('Error fetching QR code for printer', printer.id, error);
      }
    }

    // Create print-friendly page
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(generateLabelHTML(selectedPrinterData, qrCodes));
    printWindow.document.close();

    // Wait for content to load then print
    setTimeout(() => {
      printWindow.print();
    }, 1000);
  };

  const generateLabelHTML = (printers: Printer[], qrCodes: {[key: number]: string}) => {
    const labelsPerPage = 4;
    const pages = [];

    for (let i = 0; i < printers.length; i += labelsPerPage) {
      const pageLabels = printers.slice(i, i + labelsPerPage);
      pages.push(pageLabels);
    }

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Yazıcı Etiketleri</title>
        <style>
          @page {
            size: A4;
            margin: 10mm;
          }
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
          }
          .page {
            display: grid;
            grid-template-columns: 1fr 1fr;
            grid-template-rows: 1fr 1fr;
            gap: 10mm;
            height: 267mm;
            page-break-after: always;
          }
          .page:last-child {
            page-break-after: avoid;
          }
          .label {
            border: 2px solid #333;
            border-radius: 8px;
            padding: 8mm;
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            background: white;
            position: relative;
            overflow: hidden;
          }
          .label h3 {
            margin: 0 0 5mm 0;
            font-size: 32px;
            font-weight: bold;
            color: #333;
          }
          .label .qr-code {
            width: 60mm;
            height: 60mm;
            margin: 5mm 0;
            border-radius: 4px;
          }
        </style>
      </head>
      <body>
        ${pages.map(pageLabels => `
          <div class="page">
            ${pageLabels.map(printer => `
              <div class="label">
                <h3>${printer.name}</h3>
                ${qrCodes[printer.id] ?
                  `<img class="qr-code" ${qrCodes[printer.id]} alt="QR Code" />` :
                  '<div class="qr-code" style="background: #f0f0f0; display: flex; align-items: center; justify-content: center; font-size: 10px;">QR Yok</div>'
                }
              </div>
            `).join('')}
            ${Array(labelsPerPage - pageLabels.length).fill('').map(() => '<div class="label" style="border: none;"></div>').join('')}
          </div>
        `).join('')}
      </body>
      </html>
    `;
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Printer className="w-6 h-6" />
          {t('printer.title')}
        </h1>
        {activeTab === 'printers' && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowModal(true)}
              className="bg-primary text-white px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-primary-dark text-sm"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">{t('printer.btnNewPrinter')}</span>
              <span className="sm:hidden">{t('printer.btnNew')}</span>
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="bg-green-600 text-white px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700 text-sm"
            >
              <Upload className="w-4 h-4" />
              <span className="hidden sm:inline">{t('printer.btnImport')}</span>
              <span className="sm:hidden">{t('printer.btnImportShort')}</span>
            </button>
            <button
              onClick={() => setShowLabelModal(true)}
              className="bg-purple-600 text-white px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-purple-700 text-sm"
            >
              <QrCode className="w-4 h-4" />
              <span className="hidden sm:inline">{t('printer.btnBulkLabel')}</span>
              <span className="sm:hidden">{t('printer.btnLabel')}</span>
            </button>
            <button
              onClick={exportToExcel}
              className="bg-orange-600 text-white px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-orange-700 text-sm"
              title="Excel dosyası olarak dışarı aktar"
            >
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">{t('printer.btnExcel')}</span>
              <span className="sm:hidden">{t('printer.btnExcelShort')}</span>
            </button>
          </div>
        )}
      </div>

      {/* Tab Menü */}
      <div className="mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('printers')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'printers'
                  ? 'border-primary text-primary dark:text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {t('printer.tabPrinters')}
            </button>
            <button
              onClick={() => setActiveTab('brands')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'brands'
                  ? 'border-primary text-primary dark:text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {t('printer.tabBrands')}
            </button>
            <button
              onClick={() => setActiveTab('models')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'models'
                  ? 'border-primary text-primary dark:text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {t('printer.tabModels')}
            </button>
            <button
              onClick={() => setActiveTab('drivers')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'drivers'
                  ? 'border-primary text-primary dark:text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:border-gray-600 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {t('printer.tabDrivers')}
            </button>
          </nav>
        </div>
      </div>

      {/* Hata ve Başarı Mesajları */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
          {success}
        </div>
      )}

      {/* Tab İçerikleri */}
      {activeTab === 'printers' && (
        <>
          {/* İstatistik Kartları */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div 
              className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 cursor-pointer transition-all hover:shadow-lg ${
                typeFilter === 'all' ? 'ring-2 ring-primary' : ''
              }`}
              onClick={() => setTypeFilter('all')}
            >
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-primary/20">
                  <Printer className="w-6 h-6 text-primary" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('printer.statTotal')}</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{totalPrinters}</p>
                </div>
              </div>
            </div>

            <div 
              className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 cursor-pointer transition-all hover:shadow-lg ${
                typeFilter === 'office' ? 'ring-2 ring-green-500' : ''
              }`}
              onClick={() => setTypeFilter('office')}
            >
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-green-100">
                  <Printer className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('printer.statOffice')}</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{officePrinters}</p>
                </div>
              </div>
            </div>

            <div 
              className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 cursor-pointer transition-all hover:shadow-lg ${
                typeFilter === 'industrial' ? 'ring-2 ring-orange-500' : ''
              }`}
              onClick={() => setTypeFilter('industrial')}
            >
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-orange-100">
                  <Printer className="w-6 h-6 text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{t('printer.statIndustrial')}</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{industrialPrinters}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Arama ve Filtreler */}
          <div className="mb-6 space-y-4">
            {/* Arama */}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder={t('printer.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border rounded-lg w-full max-w-md"
              />
            </div>

            {/* Filtreler */}
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('printer.filters')}</h3>
                <button
                  onClick={() => {
                    setTypeFilter('all');
                    setCompanyFilter('all');
                    setBrandFilter('all');
                    setIpFilter('');
                    setSerialFilter('');
                    setLocationFilter('all');
                    setStatusFilter('all');
                  }}
                  className="text-xs text-primary hover:text-primary-dark"
                >
                  {t('printer.clearFilters')}
                </button>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {/* Firma Filtresi */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('printer.filterCompany')}</label>
                  <select
                    value={companyFilter}
                    onChange={(e) => setCompanyFilter(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="all">{t('printer.filterAll')}</option>
                    <option value="no-company">{t('printer.filterNoCompany')}</option>
                    {companies.map(company => (
                      <option key={company.id} value={company.name}>{company.name}</option>
                    ))}
                  </select>
                </div>

                {/* Marka Filtresi */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('printer.filterBrand')}</label>
                  <select
                    value={brandFilter}
                    onChange={(e) => setBrandFilter(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="all">{t('printer.filterAll')}</option>
                    {brands.map(brand => (
                      <option key={brand.id} value={brand.name}>{brand.name}</option>
                    ))}
                  </select>
                </div>

                {/* IP Adresi Filtresi */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('printer.filterIp')}</label>
                  <input
                    type="text"
                    placeholder={t('printer.filterIpPlaceholder')}
                    value={ipFilter}
                    onChange={(e) => setIpFilter(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>

                {/* Seri Numarası Filtresi */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('printer.filterSerial')}</label>
                  <input
                    type="text"
                    placeholder={t('printer.filterSerialPlaceholder')}
                    value={serialFilter}
                    onChange={(e) => setSerialFilter(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>

                {/* Lokasyon Filtresi */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('printer.filterLocation')}</label>
                  <select
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="all">{t('printer.filterAll')}</option>
                    {locations.map(location => (
                      <option key={location.id} value={location.name}>{location.name}</option>
                    ))}
                  </select>
                </div>

                {/* Kategori Filtresi */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('printer.filterCategory')}</label>
                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value as 'all' | 'office' | 'industrial')}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="all">{t('printer.filterAll')}</option>
                    <option value="office">{t('printer.catOffice')}</option>
                    <option value="industrial">{t('printer.catIndustrial')}</option>
                  </select>
                </div>

                {/* Durum Filtresi */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{t('printer.filterStatus')}</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                  >
                    <option value="all">{t('printer.filterAll')}</option>
                    <option value="Aktif">{t('printer.statusActive')}</option>
                    <option value="Pasif">{t('printer.statusPassive')}</option>
                    <option value="Sözleşme Süresi Dolmuş">{t('printer.statusContractExpired')}</option>
                    <option value="Arızalı">{t('printer.statusFaulty')}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
            <table className="w-full" style={{minWidth: '1200px'}}>
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th 
                    className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none w-48"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      {t('printer.colName')}
                      <SortIcon field="name" />
                    </div>
                  </th>
                  <th 
                    className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none w-32 hide-on-mobile"
                    onClick={() => handleSort('company')}
                  >
                    <div className="flex items-center gap-1">
                      {t('printer.colCompany')}
                      <SortIcon field="company" />
                    </div>
                  </th>
                  <th 
                    className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none w-32 hide-on-mobile"
                    onClick={() => handleSort('brand')}
                  >
                    <div className="flex items-center gap-1">
                      {t('printer.colBrandModel')}
                      <SortIcon field="brand" />
                    </div>
                  </th>
                  <th 
                    className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none w-24 hide-on-mobile"
                    onClick={() => handleSort('type')}
                  >
                    <div className="flex items-center gap-1">
                      {t('printer.colType')}
                      <SortIcon field="type" />
                    </div>
                  </th>
                  <th 
                    className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none w-24 hide-on-mobile"
                    onClick={() => handleSort('category')}
                  >
                    <div className="flex items-center gap-1">
                      {t('printer.colCategory')}
                      <SortIcon field="category" />
                    </div>
                  </th>
                  <th 
                    className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none w-32"
                    onClick={() => handleSort('ip_address')}
                  >
                    <div className="flex items-center gap-1">
                      {t('printer.colIp')}
                      <SortIcon field="ip_address" />
                    </div>
                  </th>
                  <th
                    className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none w-32 hide-on-mobile"
                    onClick={() => handleSort('mac_address')}
                  >
                    <div className="flex items-center gap-1">
                      {t('printer.colMac')}
                      <SortIcon field="mac_address" />
                    </div>
                  </th>
                  <th
                    className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none w-32 hide-on-mobile"
                    onClick={() => handleSort('serial_number')}
                  >
                    <div className="flex items-center gap-1">
                      {t('printer.colSerial')}
                      <SortIcon field="serial_number" />
                    </div>
                  </th>
                  <th 
                    className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none w-32"
                    onClick={() => handleSort('location')}
                  >
                    <div className="flex items-center gap-1">
                      {t('printer.colLocation')}
                      <SortIcon field="location" />
                    </div>
                  </th>
                  <th 
                    className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none w-24 hide-on-mobile"
                    onClick={() => handleSort('department')}
                  >
                    <div className="flex items-center gap-1">
                      {t('printer.colDepartment')}
                      <SortIcon field="department" />
                    </div>
                  </th>
                  <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-32 hide-on-mobile">{t('printer.colCounters')}</th>
                  <th 
                    className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none w-24"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center gap-1">
                      {t('printer.colStatus')}
                      <SortIcon field="status" />
                    </div>
                  </th>
                  <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase w-32">{t('printer.colActions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {paginatedPrinters.map((printer) => (
                  <tr key={printer.id}>
                    <td className="px-2 sm:px-6 py-3">
                      <div className="flex items-center">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary/20 rounded-lg flex items-center justify-center mr-2 sm:mr-3">
                          <Printer className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{printer.name}</div>
                          {printer.alias && (
                            <div className="text-xs text-indigo-600 dark:text-indigo-400 font-medium truncate">{printer.alias}</div>
                          )}
                          <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">{printer.brand} {printer.model}</div>
                          {/* Mobilde şirket ve tip bilgilerini göster */}
                          <div className="sm:hidden mt-1 space-y-1">
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {t('printer.mobileCompany')}: {printer.company || '-'}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {t('printer.mobileType')}: {printer.type}
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-2 sm:px-6 py-3 whitespace-nowrap hide-on-mobile">
                      <div className="text-xs sm:text-sm text-gray-900 dark:text-gray-100">{printer.company || '-'}</div>
                    </td>
                    <td className="px-2 sm:px-6 py-3 whitespace-nowrap hide-on-mobile">
                      <div className="text-xs sm:text-sm text-gray-900 dark:text-gray-100">{printer.brand} {printer.model}</div>
                    </td>
                    <td className="px-2 sm:px-6 py-3 whitespace-nowrap hide-on-mobile">
                      <span className="px-1.5 py-0.5 text-xs rounded-full bg-primary/20 text-primary-dark">
                        {printer.type}
                      </span>
                    </td>
                    <td className="px-2 sm:px-6 py-3 whitespace-nowrap hide-on-mobile">
                      <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                        printer.category === 'office' ? 'bg-green-100 text-green-800' :
                        printer.category === 'industrial' ? 'bg-orange-100 text-orange-800' :
                        printer.category === 'mobile' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                      }`}>
                        {printer.category === 'office' ? t('printer.catOffice') :
                         printer.category === 'industrial' ? t('printer.catIndustrial') :
                         printer.category === 'mobile' ? t('printer.catMobile') :
                         printer.category}
                      </span>
                    </td>
                    <td className="px-2 sm:px-6 py-3 whitespace-nowrap">
                      <div className="text-xs sm:text-sm text-gray-900 dark:text-gray-100 font-mono">{printer.ip_address}</div>
                    </td>
                    <td className="px-2 sm:px-6 py-3 whitespace-nowrap hide-on-mobile">
                      <div className="text-xs sm:text-sm text-gray-900 dark:text-gray-100 font-mono">{printer.mac_address || '-'}</div>
                    </td>
                    <td className="px-2 sm:px-6 py-3 whitespace-nowrap hide-on-mobile">
                      <div className="text-xs sm:text-sm text-gray-900 dark:text-gray-100 font-mono">{printer.serial_number || '-'}</div>
                    </td>
                    <td className="px-2 sm:px-6 py-3 whitespace-nowrap">
                      <div className="text-xs sm:text-sm text-gray-900 dark:text-gray-100 truncate">{printer.location}</div>
                    </td>
                    <td className="px-2 sm:px-6 py-3 whitespace-nowrap hide-on-mobile">
                      <div className="text-xs sm:text-sm text-gray-900 dark:text-gray-100">{printer.department || '-'}</div>
                    </td>
                    <td className="px-2 sm:px-6 py-3 whitespace-nowrap hide-on-mobile">
                      <div className="space-y-1">
                        {printer.latest_counters?.black_white && (
                          <div className="flex items-center text-xs">
                            <span className="w-2 h-2 bg-gray-800 rounded-full mr-2"></span>
                            S/B: {(printer.latest_counters.black_white || 0).toLocaleString()}
                          </div>
                        )}
                        {printer.latest_counters?.color && (
                          <div className="flex items-center text-xs">
                            <span className="w-2 h-2 bg-primary rounded-full mr-2"></span>
                            Renkli: {(printer.latest_counters.color || 0).toLocaleString()}
                          </div>
                        )}
                        {printer.latest_counters?.total && (
                          <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
                            Toplam: {(printer.latest_counters.total || 0).toLocaleString()}
                          </div>
                        )}
                        {!printer.latest_counters || Object.keys(printer.latest_counters).length === 0 && (
                          <div className="text-xs text-gray-400">{t('printer.noData')}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-2 sm:px-6 py-3 whitespace-nowrap">
                      <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                        printer.status === 'active' ? 'bg-green-100 text-green-800' :
                        printer.status === 'passive' ? 'bg-yellow-100 text-yellow-800' :
                        printer.status === 'contract_expired' ? 'bg-red-100 text-red-800' :
                        'bg-orange-100 text-orange-800'
                      }`}>
                        {printer.status === 'active' ? t('printer.statusActive') :
                         printer.status === 'passive' ? t('printer.statusPassive') :
                         printer.status === 'contract_expired' ? t('printer.statusContractExpired') :
                         printer.status}
                      </span>
                    </td>
                    <td className="px-2 sm:px-6 py-3 whitespace-nowrap">
                      <div className="flex gap-1 sm:gap-2">
                        <button
                          onClick={() => startEdit(printer)}
                          className="p-1 min-h-[32px] min-w-[32px] flex items-center justify-center text-primary hover:text-primary-dark"
                          title={t('printer.actionEdit')}
                        >
                          <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                        <button
                          onClick={() => quickCopy(printer)}
                          className="p-1 min-h-[32px] min-w-[32px] flex items-center justify-center text-orange-600 hover:text-orange-800"
                          title={t('printer.actionCopy')}
                        >
                          <Copy className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                        <button
                          onClick={() => openCounterModal(printer)}
                          className="p-1 min-h-[32px] min-w-[32px] flex items-center justify-center text-green-600 hover:text-green-800"
                          title={t('printer.actionCounter')}
                        >
                          <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                        <button
                          onClick={() => openHistory(printer)}
                          className="p-1 min-h-[32px] min-w-[32px] flex items-center justify-center text-purple-600 hover:text-purple-800"
                          title={t('printer.actionHistory')}
                        >
                          <History className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                        <button
                          onClick={() => openTonerModal(printer)}
                          className="p-1 min-h-[32px] min-w-[32px] flex items-center justify-center text-indigo-600 hover:text-indigo-800"
                          title={t('printer.actionToner')}
                        >
                          <Package className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                        <button
                          onClick={() => generateQRCode(printer)}
                          className="p-1 min-h-[32px] min-w-[32px] flex items-center justify-center text-cyan-600 hover:text-cyan-800"
                          title={t('printer.actionQR')}
                        >
                          <QrCode className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                        {printer.contract_file_path && (
                          <button
                            onClick={() => downloadContract(printer)}
                            className="p-1 min-h-[32px] min-w-[32px] flex items-center justify-center text-indigo-600 hover:text-indigo-800"
                            title={t('printer.actionContract')}
                          >
                            <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(printer.id)}
                          className="p-1 min-h-[32px] min-w-[32px] flex items-center justify-center text-red-600 hover:text-red-800"
                          title={t('printer.actionDelete')}
                        >
                          <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Önceki
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Sonraki
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <span className="font-medium">{startIndex + 1}</span>
                    {' - '}
                    <span className="font-medium">{Math.min(endIndex, filteredPrinters.length)}</span>
                    {' / '}
                    <span className="font-medium">{filteredPrinters.length}</span>
                    {' sonuçtan'}
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Önceki</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    
                    {/* Sayfa numaraları */}
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                      // Sadece mevcut sayfa ve etrafındaki sayfaları göster
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 2 && page <= currentPage + 2)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              page === currentPage
                                ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (
                        page === currentPage - 3 ||
                        page === currentPage + 3
                      ) {
                        return (
                          <span key={page} className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-300">
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Sonraki</span>
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'brands' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Yazıcı Markaları</h3>
            <button
              onClick={() => {
                setEditingBrand(null);
                setNewBrandName('');
              }}
              className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Yeni Marka</span>
            </button>
          </div>
          <div className="p-6">
            {/* Yeni Marka Ekleme */}
            <div className="mb-6 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700">
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Yeni Marka Ekle</h4>
              <div className="flex space-x-2">
                <input
                  type="text"
                  placeholder="Marka adı girin"
                  value={newBrandName}
                  onChange={(e) => setNewBrandName(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <button
                  onClick={handleAddBrand}
                  className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-1"
                >
                  <Check className="w-4 h-4" />
                  <span>Ekle</span>
                </button>
              </div>
            </div>

            {/* Markalar Listesi */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {brands.map((brand) => (
                <div key={brand.id} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      {editingBrand?.id === brand.id ? (
                        <div className="flex space-x-2">
                          <input
                            type="text"
                            value={newBrandName}
                            onChange={(e) => setNewBrandName(e.target.value)}
                            className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                          />
                          <button
                            onClick={handleSaveBrand}
                            className="text-green-600 hover:text-green-800"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setEditingBrand(null);
                              setNewBrandName('');
                            }}
                            className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <h4 className="font-medium text-gray-900 dark:text-gray-100">{brand.name}</h4>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{brand.models.length} model</p>
                        </>
                      )}
                    </div>
                    {editingBrand?.id !== brand.id && (
                      <div className="flex space-x-1 ml-2">
                        <button
                          onClick={() => handleEditBrand(brand)}
                          className="text-primary hover:text-primary-dark"
                          title="Düzenle"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteBrand(brand.id, brand.name)}
                          className="text-red-600 hover:text-red-800"
                          title="Sil"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'models' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Yazıcı Modelleri</h3>
            <button
              onClick={() => {
                setEditingModel(null);
                setNewModelName('');
                setSelectedBrandForModel(null);
              }}
              className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Yeni Model</span>
            </button>
          </div>
          <div className="p-6">
            {/* Yeni Model Ekleme */}
            <div className="mb-6 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-700">
              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Yeni Model Ekle</h4>
              <div className="space-y-3">
                <select
                  value={selectedBrandForModel || ''}
                  onChange={(e) => setSelectedBrandForModel(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Marka seçin</option>
                  {brands.map((brand) => (
                    <option key={brand.id} value={brand.id}>{brand.name}</option>
                  ))}
                </select>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    placeholder="Model adı girin"
                    value={newModelName}
                    onChange={(e) => setNewModelName(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <button
                    onClick={handleAddModel}
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-1"
                  >
                    <Check className="w-4 h-4" />
                    <span>Ekle</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Modeller Listesi */}
            <div className="space-y-4">
              {brands.map((brand) => (
                <div key={brand.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">{brand.name}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {brand.models.map((model) => (
                      <div key={model.id} className="p-2 bg-gray-50 dark:bg-gray-700 rounded text-sm text-gray-700 dark:text-gray-300 flex justify-between items-center">
                        {editingModel?.id === model.id ? (
                          <div className="flex space-x-2 flex-1">
                            <input
                              type="text"
                              value={newModelName}
                              onChange={(e) => setNewModelName(e.target.value)}
                              className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                            <button
                              onClick={handleSaveModel}
                              className="text-green-600 hover:text-green-800"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setEditingModel(null);
                                setNewModelName('');
                                setSelectedBrandForModel(null);
                              }}
                              className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <span className="flex-1">{model.name}</span>
                            <div className="flex space-x-1">
                              <button
                                onClick={() => handleEditModel({...model, brand_id: brand.id})}
                                className="text-primary hover:text-primary-dark"
                                title="Düzenle"
                              >
                                <Edit className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => handleDeleteModel(model.id, model.name)}
                                className="text-red-600 hover:text-red-800"
                                title="Sil"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'drivers' && (
        <>
          {/* Driver İstatistik Kartları */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-primary/20">
                  <Download className="w-6 h-6 text-primary" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Toplam Driver</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{drivers.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-green-100">
                  <Download className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Toplam İndirme</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                    {drivers.reduce((sum, driver) => sum + (driver.download_count || 0), 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-purple-100">
                  <Download className="w-6 h-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Universal Driver</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                    {drivers.filter(driver => driver.is_universal).length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-orange-100">
                  <Download className="w-6 h-6 text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">En Çok İndirilen</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                    {drivers.length > 0 ? 
                      drivers.reduce((max, driver) => 
                        (driver.download_count || 0) > (max.download_count || 0) ? driver : max
                      ).download_count || 0 : 0
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Yazıcı Drivers</h3>
              <button
                onClick={() => setShowDriverModal(true)}
                className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors flex items-center space-x-2"
              >
                <Upload className="w-4 h-4" />
                <span>Driver Yükle</span>
              </button>
            </div>
            <div className="p-6">
            {/* Driver Listesi */}
            <div className="space-y-6">
              {brands.map((brand) => {
                const brandDrivers = drivers.filter(driver => driver.brand_id === brand.id);
                if (brandDrivers.length === 0) return null;
                
                return (
                  <div key={brand.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                      <Printer className="w-5 h-5" />
                      {brand.name}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {brandDrivers.map((driver) => (
                        <div key={driver.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border">
                          <div className="flex justify-between items-start mb-2">
                            <h5 className="font-medium text-gray-900 dark:text-gray-100 text-sm">{driver.name}</h5>
                            <div className="flex gap-1">
                              <button
                                onClick={() => handleDriverDownload(driver)}
                                className="text-primary hover:text-primary-dark p-1"
                                title="İndir"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDriverDelete(driver.id)}
                                className="text-red-600 hover:text-red-800 p-1"
                                title="Sil"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          
                          <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                            <div className="flex justify-between">
                              <span>Model:</span>
                              <span className="font-medium">
                                {driver.is_universal ? 'Universal' : (driver.model_name || 'Belirtilmemiş')}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Dosya:</span>
                              <span className="font-medium">{driver.filename}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Boyut:</span>
                              <span className="font-medium">
                                {(driver.file_size / (1024 * 1024)).toFixed(1)} MB
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>İndirme:</span>
                              <span className="font-medium text-primary">
                                {driver.download_count || 0} kez
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Yükleyen:</span>
                              <span className="font-medium">{driver.uploader_name || 'Bilinmiyor'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Tarih:</span>
                              <span className="font-medium">
                                {new Date(driver.uploaded_at).toLocaleDateString('tr-TR')}
                              </span>
                            </div>
                          </div>
                          
                          {driver.description && (
                            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 p-2 rounded border">
                              {driver.description}
                            </div>
                          )}
                          
                          {driver.is_universal && (
                            <div className="mt-2">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Universal Driver
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              
              {drivers.length === 0 && (
                <div className="text-center py-12">
                  <Printer className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Henüz driver yüklenmemiş</h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-4">Yazıcı driver'larını yüklemek için yukarıdaki butonu kullanın.</p>
                  <button
                    onClick={() => setShowDriverModal(true)}
                    className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors flex items-center space-x-2 mx-auto"
                  >
                    <Upload className="w-4 h-4" />
                    <span>İlk Driver'ı Yükle</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        </>
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[95vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {editingPrinter ? 'Yazıcı Düzenle' : 'Yeni Yazıcı Ekle'}
              </h2>
              <button
                onClick={resetForm}
                className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Yazıcı Adı */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Yazıcı Adı *
                </label>
                <input
                  type="text"
                  placeholder={user?.location ? generatePrinterName(user.location.name) : "TR-ALI-PRT-001"}
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  required
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Format: {user?.location ? generatePrinterName(user.location.name) : "LOKASYON-PRT-001"}
                </p>
              </div>

              {/* Takma Ad */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Takma Ad
                </label>
                <input
                  type="text"
                  placeholder="Satış Departmanı Yazıcısı"
                  value={formData.alias}
                  onChange={(e) => setFormData({...formData, alias: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>

              {/* Marka ve Model */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Marka *
                  </label>
                  <select
                    value={formData.brand}
                    onChange={(e) => setFormData({...formData, brand: e.target.value, model: ''})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    required
                  >
                    <option value="">Marka Seçin</option>
                    {brands.map(brand => (
                      <option key={brand.id} value={brand.name}>{brand.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Model *
                  </label>
                  <select
                    value={formData.model}
                    onChange={(e) => setFormData({...formData, model: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    required
                    disabled={!selectedBrand}
                  >
                    <option value="">Model Seçin</option>
                    {selectedBrand?.models.map(model => (
                      <option key={model.id} value={model.name}>{model.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Tip ve Kategori */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Yazıcı Tipi
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="Lazer">Lazer</option>
                    <option value="Inkjet">Inkjet</option>
                    <option value="Dot Matrix">Dot Matrix</option>
                    <option value="Thermal">Thermal</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Kategori
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="office">Ofis</option>
                    <option value="industrial">Endüstriyel</option>
                    <option value="mobile">Mobil</option>
                  </select>
                </div>
              </div>

              {/* Durum */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Durum
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="active">Aktif</option>
                  <option value="passive">Pasif</option>
                  <option value="contract_expired">Sözleşme Süresi Dolmuş</option>
                </select>
              </div>

              {/* IP Adresi ve MAC Adresi */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    IP Adresi
                  </label>
                  <IPAddressInput
                    value={formData.ip_address}
                    onChange={(value) => {
                      setFormData({...formData, ip_address: value});
                      // Clear IP warnings when user types
                      setDuplicateWarnings(prev => prev.filter(w => w.type !== 'ip_address'));
                    }}
                    className="justify-start"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Örnek: 192.168.1.111
                  </p>
                  {duplicateWarnings.some(w => w.type === 'ip_address') && (
                    <p className="text-xs text-red-500 mt-1">
                      {duplicateWarnings.find(w => w.type === 'ip_address')?.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    MAC Adresi
                  </label>
                  <input
                    type="text"
                    placeholder="Örn: 00:1A:2B:3C:4D:5E"
                    value={formData.mac_address}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase();
                      setFormData({...formData, mac_address: value});
                      // Clear MAC address warnings when user types
                      setDuplicateWarnings(prev => prev.filter(w => w.type !== 'mac_address'));
                    }}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Format: XX:XX:XX:XX:XX:XX (Örn: 00:1A:2B:3C:4D:5E)
                  </p>
                  {duplicateWarnings.some(w => w.type === 'mac_address') && (
                    <p className="text-xs text-red-500 mt-1">
                      {duplicateWarnings.find(w => w.type === 'mac_address')?.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Seri Numarası ve Lokasyon */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Seri Numarası
                  </label>
                  <input
                    type="text"
                    placeholder="Örn: ABC123456"
                    value={formData.serial_number}
                    onChange={(e) => {
                      setFormData({...formData, serial_number: e.target.value});
                      // Clear serial number warnings when user types
                      setDuplicateWarnings(prev => prev.filter(w => w.type !== 'serial_number'));
                    }}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                  {duplicateWarnings.some(w => w.type === 'serial_number') && (
                    <p className="text-xs text-red-500 mt-1">
                      {duplicateWarnings.find(w => w.type === 'serial_number')?.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Lokasyon *
                  </label>
                  <select
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    required
                  >
                    <option value="">Lokasyon Seçin</option>
                    {locations.map(location => (
                      <option key={location.id} value={location.name}>{location.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Bölüm */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Bölüm
                </label>
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => setFormData({...formData, department: e.target.value})}
                  placeholder="IT, Muhasebe, İnsan Kaynakları vb."
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Yazıcının kullanıldığı bölüm veya departman
                </p>
              </div>

              {/* Firma Seçimi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Firma
                </label>
                <select
                  value={formData.company_id}
                  onChange={(e) => setFormData({...formData, company_id: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="">Firma Seçin (Opsiyonel)</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Yazıcının bağlı olduğu firma
                </p>
              </div>

              {/* Sözleşme Tarihleri */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Sözleşme Başlangıç
                  </label>
                  <input
                    type="date"
                    value={formData.contract_start_date}
                    onChange={(e) => setFormData({...formData, contract_start_date: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Sözleşme Bitiş
                  </label>
                  <input
                    type="date"
                    value={formData.contract_end_date}
                    onChange={(e) => setFormData({...formData, contract_end_date: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
              </div>

              {/* Sözleşme Dosyası */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Sözleşme Dosyası
                </label>
                
                {/* Mevcut dosya varsa göster */}
                {editingPrinter?.contract_file_path && (
                  <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center">
                          <Download className="w-5 h-5 text-green-600 dark:text-green-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-green-800 dark:text-green-200">
                            Sözleşme dosyası mevcut
                          </p>
                          <p className="text-xs text-green-600 dark:text-green-400">
                            {editingPrinter.contract_file_path.split('/').pop()}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => downloadContract(editingPrinter)}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
                      >
                        İndir
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Dosya yükleme alanı */}
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file && editingPrinter) {
                        setContractFile(file);
                        await uploadContractFile(file, editingPrinter.id);
                      } else {
                        setContractFile(file);
                      }
                    }}
                    className="hidden"
                    id="contract-file"
                    disabled={uploadingContract}
                  />
                  <label
                    htmlFor="contract-file"
                    className={`flex flex-col items-center space-y-2 ${uploadingContract ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                  >
                    <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                      {uploadingContract ? (
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                      ) : (
                        <Plus className="w-6 h-6 text-gray-400" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {uploadingContract ? 'Yükleniyor...' : 
                         contractFile ? contractFile.name : 
                         (editingPrinter?.contract_file_path ? 'Yeni Dosya Seçin' : 'Dosya Seçin')}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {uploadingContract ? 'Sözleşme dosyası yükleniyor, lütfen bekleyin...' : 
                         'PDF, DOC, DOCX formatları desteklenir'}
                        {editingPrinter?.contract_file_path && !uploadingContract && (
                          <span className="block mt-1 text-orange-600 dark:text-orange-400">
                            Yeni dosya seçerseniz mevcut dosya değiştirilir
                          </span>
                        )}
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Butonlar */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-primary text-white py-3 px-6 rounded-lg hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-colors font-medium"
                >
                  {editingPrinter ? 'Güncelle' : 'Yazıcı Ekle'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 py-3 px-6 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors font-medium"
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
        </Modal>


      {/* Sayaç Girişi Modal */}
      <Modal isOpen={showCounterModal} onClose={() => setShowCounterModal(false)}>
          {selectedPrinter && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">
              {selectedPrinter.name} - Sayaç Bilgisi Ekle
            </h2>
            <form onSubmit={handleCounterSubmit} className="space-y-4">
              <select
                value={counterData.counter_type}
                onChange={(e) => setCounterData({...counterData, counter_type: e.target.value as any})}
                className="w-full p-2 border rounded"
                required
              >
                <option value="black_white">Siyah Beyaz</option>
                <option value="color">Renkli</option>
                <option value="total">Toplam</option>
              </select>
              <input
                type="number"
                placeholder="Sayaç Değeri"
                value={counterData.counter_value}
                onChange={(e) => setCounterData({...counterData, counter_value: e.target.value})}
                className="w-full p-2 border rounded"
                required
                min="0"
              />
              <input
                type="date"
                value={counterData.recorded_date}
                onChange={(e) => setCounterData({...counterData, recorded_date: e.target.value})}
                className="w-full p-2 border rounded"
                required
              />
              <textarea
                placeholder="Notlar (opsiyonel)"
                value={counterData.notes}
                onChange={(e) => setCounterData({...counterData, notes: e.target.value})}
                className="w-full p-2 border rounded"
                rows={3}
              />
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="flex-1 bg-green-600 text-white py-2 rounded hover:bg-green-700"
                  disabled={!counterData.counter_value}
                >
                  Sayaç Ekle
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCounterModal(false);
                    setSelectedPrinter(null);
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 dark:text-gray-300 py-2 rounded hover:bg-gray-400"
                >
                  İptal
                </button>
              </div>
            </form>
          </div>
          )}
        </Modal>

      {/* Sayaç Geçmişi Modal */}
      <Modal isOpen={showCounterHistory} onClose={() => setShowCounterHistory(false)}>
          {selectedPrinter && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">
              {selectedPrinter.name} - İşlem Geçmişi
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-gray-900 dark:text-gray-100">Tarih</th>
                    <th className="px-4 py-2 text-left text-gray-900 dark:text-gray-100">İşlem Türü</th>
                    <th className="px-4 py-2 text-left text-gray-900 dark:text-gray-100">Açıklama</th>
                    <th className="px-4 py-2 text-left text-gray-900 dark:text-gray-100">Yapan</th>
                    <th className="px-4 py-2 text-left text-gray-900 dark:text-gray-100">Detaylar</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 dark:divide-gray-600">
                  {printerHistory.map((record) => {
                    const date = record.source_type === 'counter' 
                      ? new Date(record.recorded_date).toLocaleDateString('tr-TR')
                      : new Date(record.performed_at).toLocaleDateString('tr-TR');
                    
                    let typeLabel = '';
                    let description = '';
                    let details = '';
                    
                    if (record.source_type === 'counter') {
                      typeLabel = 'Sayaç Kaydı';
                      description = `${record.counter_type === 'black_white' ? 'Siyah Beyaz' : record.counter_type === 'color' ? 'Renkli' : 'Toplam'}: ${(record.counter_value || 0).toLocaleString()}`;
                      details = record.notes || '-';
                    } else {
                      const actionTypeMap: {[key: string]: string} = {
                        'toner_change': 'Toner Değişimi',
                        'maintenance': 'Bakım',
                        'repair': 'Onarım',
                        'status_change': 'Durum Değişikliği',
                        'location_change': 'Lokasyon Değişikliği',
                        'other': 'Diğer'
                      };
                      typeLabel = actionTypeMap[record.action_type] || record.action_type;
                      description = record.action_description || '-';
                      
                      if (record.action_type === 'status_change' && record.old_data?.ip_address !== undefined) {
                        details = `${record.old_data.ip_address || 'Belirtilmemiş'} → ${record.new_data?.ip_address || 'Belirtilmemiş'}`;
                      } else if (record.action_type === 'toner_change' && record.new_data) {
                        details = `${record.new_data.consumable_name} (${record.new_data.quantity} adet)`;
                      } else {
                        details = record.notes || '-';
                      }
                    }
                    
                    return (
                      <tr key={record.id} className="hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700">
                        <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{date}</td>
                        <td className="px-4 py-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            record.source_type === 'counter' ? 'bg-primary/20 text-primary-dark dark:bg-primary/90 dark:text-accent' :
                            record.action_type === 'toner_change' ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200' :
                            record.action_type === 'status_change' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                            'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                          }`}>
                            {typeLabel}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{description}</td>
                        <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
                          {record.source_type === 'counter' ? record.recorded_by_name : record.performed_by_name || '-'}
                        </td>
                        <td className="px-4 py-2 text-gray-900 dark:text-gray-100">{details}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {printerHistory.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Henüz işlem geçmişi bulunmuyor
                </div>
              )}
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setShowCounterHistory(false);
                  setSelectedPrinter(null);
                  setPrinterHistory([]);
                }}
                className="bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded hover:bg-gray-400 dark:hover:bg-gray-500"
              >
                Kapat
              </button>
            </div>
          </div>
          )}
        </Modal>

      {/* Toner Takma Modal */}
      <Modal isOpen={showTonerModal} onClose={() => setShowTonerModal(false)}>
          {selectedPrinter && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100">
              {selectedPrinter.name} - Toner Tak
            </h2>
            
            {error && (
              <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-200 rounded">
                {error}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Toner Seçin *
                </label>
                <select
                  value={selectedTonerId || ''}
                  onChange={(e) => setSelectedTonerId(parseInt(e.target.value) || null)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  required
                >
                  <option value="">Toner Seçin</option>
                  {availableToners.map((toner) => (
                    <option key={toner.id} value={toner.id}>
                      {toner.name} {toner.brand && `(${toner.brand})`} - Stok: {toner.available_quantity}
                    </option>
                  ))}
                </select>
                {availableToners.length === 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Mevcut toner bulunamadı
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Miktar *
                </label>
                <input
                  type="number"
                  min="1"
                  value={tonerQuantity}
                  onChange={(e) => setTonerQuantity(parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notlar (Opsiyonel)
                </label>
                <textarea
                  value={tonerNotes}
                  onChange={(e) => setTonerNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-gray-100"
                  rows={3}
                  placeholder="Toner takma işlemi hakkında notlar..."
                />
              </div>
            </div>

            <div className="mt-6 flex gap-2">
              <button
                onClick={handleInstallToner}
                disabled={installingToner || !selectedTonerId || tonerQuantity < 1}
                className="flex-1 bg-indigo-600 text-white py-2 rounded hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {installingToner ? 'Takılıyor...' : 'Toner Tak'}
              </button>
              <button
                onClick={() => {
                  setShowTonerModal(false);
                  setSelectedPrinter(null);
                  setSelectedTonerId(null);
                  setTonerQuantity(1);
                  setTonerNotes('');
                  setError(null);
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 py-2 rounded hover:bg-gray-400 dark:hover:bg-gray-500"
              >
                İptal
              </button>
            </div>
          </div>
          )}
        </Modal>

      {/* Import Modal */}
      <Modal isOpen={showImportModal} onClose={() => setShowImportModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Excel'den Yazıcı İçeri Aktar
              </h2>
              <button
                onClick={() => {
                  setShowImportModal(false);
                  resetImportModal();
                }}
                className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Kullanım Talimatları:
                </h3>
                <div className="bg-primary/10 dark:bg-primary/20 p-4 rounded-lg">
                  <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700 dark:text-gray-300">
                    <li>Excel'de yazıcı verilerinizi hazırlayın</li>
                    <li>Verileri seçip kopyalayın (Ctrl+C)</li>
                    <li>Aşağıdaki alana yapıştırın (Ctrl+V)</li>
                    <li>İlk satır başlık satırı olmalıdır</li>
                  </ol>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Desteklenen Sütun Adları (Akıllı Tanıma):
                </h3>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">Yazıcı Adı:</h4>
                      <p className="text-gray-600 dark:text-gray-400">yazıcı, printer, name, adı, ad, yazici, yazıcı adı, printer name</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">Marka:</h4>
                      <p className="text-gray-600 dark:text-gray-400">marka, brand, manufacturer, üretici, uretici, firma, company</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">Model:</h4>
                      <p className="text-gray-600 dark:text-gray-400">model, modeli, model adı, model name, model_name, model no, modelno, model numarası</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">IP Adresi:</h4>
                      <p className="text-gray-600 dark:text-gray-400">ip, ip adresi, ip_address, ipadresi, ip adres, adres, address</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">Seri Numarası:</h4>
                      <p className="text-gray-600 dark:text-gray-400">seri, serial, seri numarası, serial_number, serino, seri no, serial no, sn</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">Lokasyon:</h4>
                      <p className="text-gray-600 dark:text-gray-400">lokasyon, location, konum, yer, place, lokasyonu, konumu</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">Durum:</h4>
                      <p className="text-gray-600 dark:text-gray-400">durum, status, state, hal, durumu, statü, statu</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-2">Firma:</h4>
                      <p className="text-gray-600 dark:text-gray-400">firma, company, şirket, sirket, kurum, organization, org</p>
                    </div>
                  </div>
                  <div className="mt-3 p-3 bg-primary/20 dark:bg-primary/30 rounded">
                    <p className="text-primary-dark dark:text-accent text-sm">
                      💡 <strong>Akıllı Tanıma:</strong> Sistem sütun adlarını otomatik olarak tanır. 
                      Benzer kelimeler, kısaltmalar ve farklı dillerdeki karşılıkları destekler.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Excel Verilerini Yapıştırın:
                </label>
                <textarea
                  value={importData}
                  onChange={(e) => handleDataChange(e.target.value)}
                  placeholder="Excel'den kopyaladığınız verileri buraya yapıştırın..."
                  className="w-full h-64 p-4 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-gray-700 dark:text-gray-100 font-mono text-sm"
                />
              </div>

              {/* Önizleme Bölümü */}
              {importData.trim() && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    Veri Önizlemesi:
                  </h3>
                  
                  {/* Sütun Eşleştirme Bilgisi */}
                  {Object.keys(columnMapping).length > 0 && (
                    <div className="bg-primary/10 dark:bg-primary/20 p-4 rounded-lg mb-4">
                      <p className="text-primary-dark dark:text-accent font-semibold mb-2">
                        📋 Sütun Eşleştirmesi:
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        {Object.entries(columnMapping).map(([field, column]) => {
                          const fieldNames: {[key: string]: string} = {
                            name: 'Yazıcı Adı',
                            brand: 'Marka',
                            model: 'Model',
                            type: 'Tip',
                            ip_address: 'IP Adresi',
                            serial_number: 'Seri Numarası',
                            location: 'Lokasyon',
                            status: 'Durum',
                            company: 'Firma'
                          };
                          return (
                            <div key={field} className="flex items-center justify-between bg-white dark:bg-gray-800 dark:bg-gray-700 px-3 py-2 rounded">
                              <span className="text-gray-700 dark:text-gray-300 font-medium">{fieldNames[field]}:</span>
                              <span className="text-gray-900 dark:text-gray-100">{column}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Hata Mesajları */}
                  {parseErrors.length > 0 && (
                    <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-4">
                      <p className="text-red-800 dark:text-red-200 font-semibold mb-2">
                        ❌ Parse Hataları:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-red-700 dark:text-red-300">
                        {parseErrors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Başarılı Parse Edilen Veriler */}
                  {parsedData.length > 0 && (
                    <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg mb-4">
                      <p className="text-green-800 dark:text-green-200 font-semibold mb-2">
                        ✅ {parsedData.length} yazıcı başarıyla parse edildi
                      </p>
                      {parseErrors.length > 0 && (
                        <p className="text-green-700 dark:text-green-300 text-sm">
                          ⚠️ {parseErrors.length} satırda hata var, sadece geçerli satırlar import edilecek
                        </p>
                      )}
                    </div>
                  )}

                  {/* Veri Tablosu */}
                  {parsedData.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 dark:border-gray-600 rounded-lg">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase">Satır</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase">Yazıcı Adı</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase">Marka</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase">Model</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase">Tip</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase">IP</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase">Lokasyon</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase">Bölüm</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 dark:text-gray-300 uppercase">Durum</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700 dark:divide-gray-600">
                          {parsedData.slice(0, 10).map((printer, index) => (
                            <tr key={index} className="hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700">
                              <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{printer.rowNumber}</td>
                              <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100 font-medium">{printer.name}</td>
                              <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{printer.brand}</td>
                              <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{printer.model}</td>
                              <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{printer.type}</td>
                              <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{printer.ip_address || '-'}</td>
                              <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{printer.location}</td>
                              <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{printer.department || '-'}</td>
                              <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{printer.status}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {parsedData.length > 10 && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 text-center">
                          ... ve {parsedData.length - 10} yazıcı daha
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {importResults && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    İçeri Aktarma Sonuçları:
                  </h3>
                  <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg mb-3">
                    <p className="text-green-800 dark:text-green-200">
                      ✅ {importResults.success} yazıcı başarıyla eklendi
                    </p>
                  </div>
                  {importResults.errors && importResults.errors.length > 0 && (
                    <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                      <p className="text-red-800 dark:text-red-200 font-semibold mb-2">
                        ❌ Hatalar:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-red-700 dark:text-red-300">
                        {importResults.errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Buttons */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    resetImportModal();
                  }}
                  className="bg-gray-300 text-gray-700 dark:text-gray-300 px-4 py-2 rounded hover:bg-gray-400"
                >
                  İptal
                </button>
                <button
                  onClick={handleImport}
                  disabled={importing || !importData.trim() || parsedData.length === 0}
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {importing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      İçeri Aktarılıyor...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      İçeri Aktar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </Modal>

      {/* Label Printing Modal */}
      <Modal isOpen={showLabelModal} onClose={() => setShowLabelModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <QrCode className="h-6 w-6" />
                Toplu Etiket Yazdırma
              </h2>
              <button
                onClick={() => {
                  setShowLabelModal(false);
                  setSelectedPrinters([]);
                  setShowLabelPreview(false);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {!showLabelPreview ? (
                <>
                  {/* Filters */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                      Filtreler
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Lokasyon
                        </label>
                        <select
                          value={labelFilters.location}
                          onChange={(e) => setLabelFilters({...labelFilters, location: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
                        >
                          <option value="all">Tüm Lokasyonlar</option>
                          {[...new Set(printers.map(p => p.location))].map(location => (
                            <option key={location} value={location}>{location}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Tip
                        </label>
                        <select
                          value={labelFilters.type}
                          onChange={(e) => setLabelFilters({...labelFilters, type: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
                        >
                          <option value="all">Tüm Tipler</option>
                          {[...new Set(printers.map(p => p.type))].map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Kategori
                        </label>
                        <select
                          value={labelFilters.category}
                          onChange={(e) => setLabelFilters({...labelFilters, category: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
                        >
                          <option value="all">Tüm Kategoriler</option>
                          {[...new Set(printers.map(p => p.category))].map(category => (
                            <option key={category} value={category}>
                              {category === 'office' ? 'Ofis' :
                               category === 'industrial' ? 'Endüstriyel' :
                               category === 'mobile' ? 'Mobil' : category}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="mt-4">
                      <button
                        onClick={applyLabelFilters}
                        className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center gap-2"
                      >
                        <Search className="h-4 w-4" />
                        Filtrele
                      </button>
                    </div>
                  </div>

                  {/* Printer Selection */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Yazıcı Seçimi ({selectedPrinters.length} seçili)
                      </h3>
                      <div className="flex gap-2">
                        <button
                          onClick={selectAllFiltered}
                          className="text-sm bg-primary text-white px-3 py-1 rounded hover:bg-primary-dark"
                        >
                          Tümünü Seç
                        </button>
                        <button
                          onClick={deselectAll}
                          className="text-sm bg-gray-600 text-white px-3 py-1 rounded hover:bg-gray-700"
                        >
                          Seçimi Temizle
                        </button>
                      </div>
                    </div>

                    <div className="max-h-64 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg">
                      <div className="grid grid-cols-1 gap-2 p-4">
                        {(filteredPrintersForLabel.length > 0 ? filteredPrintersForLabel : printers).map(printer => (
                          <label
                            key={printer.id}
                            className="flex items-center space-x-3 p-3 hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 rounded-lg cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedPrinters.includes(printer.id)}
                              onChange={() => handlePrinterSelection(printer.id)}
                              className="rounded border-gray-300 dark:border-gray-600 text-purple-600 focus:ring-purple-500"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 dark:text-gray-100">
                                {printer.name}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {printer.brand} {printer.model} - {printer.location} - {printer.type}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setShowLabelModal(false);
                        setSelectedPrinters([]);
                      }}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                    >
                      İptal
                    </button>
                    <button
                      onClick={generateLabelPreview}
                      disabled={selectedPrinters.length === 0}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <Eye className="h-4 w-4" />
                      Önizleme
                    </button>
                  </div>
                </>
              ) : (
                /* Preview */
                <div>
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      Etiket Önizlemesi ({selectedPrinters.length} adet)
                    </h3>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {Math.ceil(selectedPrinters.length / 6)} sayfa, sayfa başına 6 etiket
                    </div>
                  </div>

                  <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg mb-4 max-h-96 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-4">
                      {printers.filter(p => selectedPrinters.includes(p.id)).map((printer, index) => (
                        <div key={printer.id} className="bg-white dark:bg-gray-800 p-4 rounded-lg border-2 border-gray-300 dark:border-gray-600 text-center">
                          <h4 className="font-bold text-sm mb-2">{printer.name}</h4>
                          <div className="text-xs space-y-1">
                            <div>{printer.brand} - {printer.model}</div>
                            <div>Tip: {printer.type}</div>
                            <div>IP: {printer.ip_address || 'Yok'}</div>
                            <div>S/N: {printer.serial_number || 'Yok'}</div>
                          </div>
                          <div className="w-16 h-16 bg-gray-200 dark:bg-gray-600 mx-auto my-2 rounded flex items-center justify-center text-xs">
                            QR
                          </div>
                          <div className="font-bold text-primary text-xs">{printer.location}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowLabelPreview(false)}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300"
                    >
                      Geri
                    </button>
                    <button
                      onClick={printLabels}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                    >
                      <Printer className="h-4 w-4" />
                      Yazdır
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Modal>

      {/* Driver Upload Modal */}
      <Modal isOpen={showDriverModal} onClose={() => setShowDriverModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[95vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Driver Yükle
              </h2>
              <button
                onClick={() => {
                  setShowDriverModal(false);
                  setDriverFile(null);
                  setDriverName('');
                  setDriverDescription('');
                  setSelectedDriverBrand(null);
                  setSelectedDriverModel(null);
                  setIsUniversalDriver(false);
                  setIsDragOver(false);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Form */}
            <div className="p-6 space-y-6">
              {/* Driver Adı */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Driver Adı *
                </label>
                <input
                  type="text"
                  placeholder="Örn: HP LaserJet Pro M404n Driver"
                  value={driverName}
                  onChange={(e) => setDriverName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  required
                />
              </div>

              {/* Marka Seçimi */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Marka *
                </label>
                <select
                  value={selectedDriverBrand || ''}
                  onChange={(e) => {
                    setSelectedDriverBrand(Number(e.target.value));
                    setSelectedDriverModel(null);
                  }}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  required
                >
                  <option value="">Marka Seçin</option>
                  {brands.map(brand => (
                    <option key={brand.id} value={brand.id}>{brand.name}</option>
                  ))}
                </select>
              </div>

              {/* Universal Driver Checkbox */}
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="isUniversal"
                  checked={isUniversalDriver}
                  onChange={(e) => {
                    setIsUniversalDriver(e.target.checked);
                    if (e.target.checked) {
                      setSelectedDriverModel(null);
                    }
                  }}
                  className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label htmlFor="isUniversal" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Universal Driver (Bu markanın tüm yazıcılarında kullanılabilir)
                </label>
              </div>

              {/* Model Seçimi (Universal değilse) */}
              {!isUniversalDriver && selectedDriverBrand && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Model (Opsiyonel)
                  </label>
                  <select
                    value={selectedDriverModel || ''}
                    onChange={(e) => setSelectedDriverModel(Number(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">Model Seçin (Opsiyonel)</option>
                    {brands.find(b => b.id === selectedDriverBrand)?.models.map(model => (
                      <option key={model.id} value={model.id}>{model.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Açıklama */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Açıklama (Opsiyonel)
                </label>
                <textarea
                  placeholder="Driver hakkında ek bilgiler..."
                  value={driverDescription}
                  onChange={(e) => setDriverDescription(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>

              {/* Dosya Yükleme */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Driver Dosyası * (.zip, .rar veya .exe)
                </label>
                <div 
                  className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-lg transition-colors ${
                    isDragOver 
                      ? 'border-primary bg-primary/10 dark:bg-primary/20' 
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                  }`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className="space-y-1 text-center">
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600 dark:text-gray-400">
                      <label
                        htmlFor="driver-file"
                        className="relative cursor-pointer bg-white dark:bg-gray-800 rounded-md font-medium text-primary hover:text-primary focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary"
                      >
                        <span>Dosya seç</span>
                        <input
                          id="driver-file"
                          name="driver-file"
                          type="file"
                          accept=".zip,.rar,.exe"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setDriverFile(file);
                            }
                          }}
                          className="sr-only"
                        />
                      </label>
                      <p className="pl-1">veya sürükleyip bırakın</p>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Maksimum 200MB, sadece .zip, .rar ve .exe dosyaları
                    </p>
                    {driverFile && (
                      <p className="text-sm text-green-600 font-medium">
                        Seçilen: {driverFile.name} ({(driverFile.size / (1024 * 1024)).toFixed(1)} MB)
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Butonlar */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowDriverModal(false);
                    setDriverFile(null);
                    setDriverName('');
                    setDriverDescription('');
                    setSelectedDriverBrand(null);
                    setSelectedDriverModel(null);
                    setIsUniversalDriver(false);
                    setIsDragOver(false);
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                >
                  İptal
                </button>
                <button
                  type="button"
                  onClick={handleDriverUpload}
                  disabled={uploadingDriver || !driverFile || !selectedDriverBrand || !driverName.trim()}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                >
                  {uploadingDriver ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Yükleniyor...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      <span>Yükle</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </Modal>
    </div>
  );
}